import React, { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';

/**
 * Renders a PDF into <canvas> elements with pdf.js.
 *
 * An <iframe> pointed at a PDF only works where the host has a PDF plugin, and
 * no phone does: Android's WebView -- the one the Shopify mobile app runs the
 * admin in -- has no PDF renderer at all, so the label came up blank, and iOS
 * shows a frozen, unscrollable first page. Being inside the admin iframe makes
 * it a nested frame, which mobile is stricter about still. Rasterising in JS
 * sidesteps all of it and looks identical on every device.
 */

/** Render width in device pixels. CSS scales the canvas down to fit. */
const TARGET_WIDTH_PX = 1400;
/** iOS Safari silently drops canvases past roughly 16 megapixels. */
const MAX_CANVAS_PIXELS = 12e6;

let pdfjsPromise = null;

/**
 * pdf.js plus its worker is around 400 KB, and most sessions never open a
 * label, so it is loaded on first preview rather than bundled into the entry.
 */
function loadPdfjs() {
  if (!pdfjsPromise) {
    pdfjsPromise = (async () => {
      // pdf.js 6 uses Promise.withResolvers, which Safari only shipped in 17.4.
      // Older phones are exactly the case this component exists for.
      if (typeof Promise.withResolvers !== 'function') {
        Promise.withResolvers = function withResolvers() {
          let resolve;
          let reject;
          const promise = new Promise((res, rej) => {
            resolve = res;
            reject = rej;
          });
          return { promise, resolve, reject };
        };
      }

      // The legacy build is the one transpiled for older mobile browsers.
      const [pdfjs, worker] = await Promise.all([
        import('pdfjs-dist/legacy/build/pdf.mjs'),
        import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?url'),
      ]);
      pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
      return pdfjs;
    })().catch((err) => {
      // Do not cache a failed load: the next open should try again.
      pdfjsPromise = null;
      throw err;
    });
  }
  return pdfjsPromise;
}

async function renderPage(page) {
  const unscaled = page.getViewport({ scale: 1 });
  let scale = TARGET_WIDTH_PX / unscaled.width;
  const pixels = unscaled.width * scale * unscaled.height * scale;
  if (pixels > MAX_CANVAS_PIXELS) scale *= Math.sqrt(MAX_CANVAS_PIXELS / pixels);

  const viewport = page.getViewport({ scale });
  const canvas = document.createElement('canvas');
  canvas.className = 'pdf-page';
  canvas.width = Math.floor(viewport.width);
  canvas.height = Math.floor(viewport.height);

  await page.render({ canvas, viewport }).promise;
  page.cleanup();
  return canvas;
}

export default function PdfCanvas({ bytes, onError }) {
  const hostRef = useRef(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    if (!bytes) return undefined;

    let cancelled = false;
    let task = null;
    setStatus('loading');

    (async () => {
      try {
        const pdfjs = await loadPdfjs();
        if (cancelled) return;

        task = pdfjs.getDocument({
          // pdf.js takes ownership of the buffer it is handed and detaches it,
          // so give it a copy -- this effect can run again on the same bytes.
          data: new Uint8Array(bytes),
          // The admin iframe's CSP has no unsafe-eval, and pdf.js compiles
          // some font and colour handling with `new Function` unless told not to.
          isEvalSupported: false,
          cMapUrl: '/pdfjs/cmaps/',
          cMapPacked: true,
          standardFontDataUrl: '/pdfjs/standard_fonts/',
        });
        const doc = await task.promise;
        if (cancelled) return;

        // HP returns one page per label, but a multi-package shipment comes back
        // as one PDF per page, so render all of them.
        const pages = document.createDocumentFragment();
        for (let n = 1; n <= doc.numPages; n += 1) {
          const page = await doc.getPage(n);
          if (cancelled) return;
          pages.appendChild(await renderPage(page));
          if (cancelled) return;
        }

        hostRef.current?.replaceChildren(pages);
        setStatus('ready');
      } catch (err) {
        if (cancelled) return;
        console.error('[pdf] Could not render the label:', err);
        setStatus('error');
        onError?.(err);
      }
    })();

    return () => {
      cancelled = true;
      // Teardown lives on the loading task, not the document: pdf.js 6 has no
      // PDFDocumentProxy.destroy(). Destroying the task also aborts a load that
      // is still in flight, and rejects the in-flight page promises -- which the
      // catch above ignores, since `cancelled` is already set.
      task?.destroy().catch(() => {});
    };
  }, [bytes, onError]);

  if (status === 'error') return null;

  return (
    <div className="pdf-viewer">
      {status === 'loading' && (
        <div className="pdf-viewer-status">
          <Loader2 size={22} className="spin" />
          <span>Rendering label…</span>
        </div>
      )}
      <div ref={hostRef} />
    </div>
  );
}
